import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Clock, Loader2, LogOut } from 'lucide-react';
import {
  otcVerify, otcStart, otcClient, otcSaveAnswers, otcHeartbeat, otcAbandon,
  clearAssessmentSeat, resolveExpiresAtMs,
} from '@/lib/otcApi';
import { fmtMMSS, classNames } from '@/lib/format';
import { toast } from 'sonner';
import { SebGate } from '@/components/SebGate';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useSessionRecording } from '@/hooks/useSessionRecording';
import { useRecordingUploadGate } from '@/hooks/useRecordingUploadGate';
import { RecordingUploadFailedModal, RecordingUploadProgress } from '@/components/RecordingUploadGate';

async function otcTechSubmit(otc, body) {
  return (await otcClient.post(`/candidate-session/${otc}/tech-assessment/submit`, body)).data;
}

function navigateAfterExit(quitUrl, reason) {
  const fallback = reason ? `/seb-quit?reason=${reason}` : '/seb-quit';
  window.location.assign(quitUrl || fallback);
}

function detailMessage(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return e?.message || fallback;
}

export default function TechAssessmentSession() {
  const { otc } = useParams();
  const { data, refetch } = useQuery({
    queryKey: ['otc-session-tech', otc],
    queryFn: () => otcVerify(otc),
    retry: false,
  });

  if (!data) {
    return <Center icon={Loader2} spin title="Verifying your invitation…" />;
  }

  return (
    <ErrorBoundary candidateMode>
      <SebGate
        otc={otc}
        sebRequired={Boolean(data.seb_required)}
        sebVerified={Boolean(data.seb_verified)}
        sebConfigUrl={data.seb_config_url}
        sebDownloadUrlWindows={data.seb_download_url_windows}
        sebDownloadUrlMacos={data.seb_download_url_macos}
        onVerified={refetch}
      >
        <TechAssessmentSessionInner otc={otc} data={data} refetch={refetch} />
      </SebGate>
    </ErrorBoundary>
  );
}

function TechAssessmentSessionInner({ otc, data, refetch }) {
  const [phase, setPhase] = useState('intro');
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [quitOpen, setQuitOpen] = useState(false);
  const [seatBlocked, setSeatBlocked] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const expiresAtRef = useRef(null);
  const submittedRef = useRef(false);
  const answersRef = useRef({});
  const saveTimerRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const terminalInFlightRef = useRef(false);
  const finalizeAssessmentRef = useRef(null);
  const runningStartedAtRef = useRef(null);
  const {
    startRecording,
    prepareRecordingBlob,
    stopAndUpload,
    stopTracksOnly,
  } = useSessionRecording(otc);
  const {
    uploadProgress,
    uploading,
    uploadFailedOpen,
    lastUploadError,
    ensureUploadBeforeTerminal,
    retryUpload,
    continueWithoutRecording,
    cancelUploadPrompt,
  } = useRecordingUploadGate(stopAndUpload, prepareRecordingBlob);

  const state = data?.state;
  const stage = data?.stage_code;
  const qs = data?.questions || [];
  const totalSeconds = data?.tech_total_seconds || data?.mcq_total_seconds || 0;
  const quitUrl = data?.seb_quit_url;

  const applyServerTimer = useCallback((payload) => {
    const rawExp = payload?.assessment?.expires_at || payload?.expires_at;
    expiresAtRef.current = resolveExpiresAtMs(rawExp, payload?.server_now, totalSeconds);
  }, [totalSeconds]);

  // Resume in-progress assessment: hydrate answers, skip intro
  useEffect(() => {
    if (hydrated || !data || data.state !== 'valid') return;
    if (data.assessment?.status === 'in_progress') {
      const map = {};
      (data.answers || []).forEach((a) => {
        if (a?.question_id != null && a.selected_index != null) {
          map[a.question_id] = a.selected_index;
        }
      });
      answersRef.current = map;
      setAnswers(map);
      applyServerTimer(data);
      setPhase('running');
    }
    setHydrated(true);
  }, [data, hydrated, applyServerTimer]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const stopBackgroundSync = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const completeTerminalSuccess = useCallback(() => {
    clearAssessmentSeat();
    setPhase('submitted');
    if (data?.seb_required) {
      navigateAfterExit(quitUrl, 'completed');
    }
  }, [data?.seb_required, quitUrl]);

  const flushAnswers = useCallback(async () => {
    const payload = Object.entries(answersRef.current).map(([qid, idx]) => ({
      question_id: qid, selected_index: idx,
    }));
    if (!payload.length) return;
    try {
      await otcSaveAnswers(otc, payload);
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      if (err === 'seat_mismatch' || err === 'seat_lease_held') {
        setSeatBlocked(e?.response?.data?.detail?.message || 'Session locked');
        return;
      }
      if (e?.response?.status === 410) {
        finalizeAssessmentRef.current?.(true);
        return;
      }
      // Network — retry on next change / heartbeat
    }
  }, [otc]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { flushAnswers(); }, 500);
  }, [flushAnswers]);

  const selectAnswer = (qid, idx) => {
    setAnswers((prev) => {
      const next = { ...prev, [qid]: idx };
      answersRef.current = next;
      return next;
    });
    scheduleSave();
  };

  useEffect(() => {
    if (phase !== 'running' || !expiresAtRef.current) return undefined;
    const tick = () => {
      const remain = Math.max(0, Math.round((expiresAtRef.current - Date.now()) / 1000));
      setSecondsLeft(remain);
      if (remain <= 0 && !submittedRef.current && !terminalInFlightRef.current) {
        finalizeAssessmentRef.current?.(true);
      }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'running') return undefined;
    runningStartedAtRef.current = Date.now();
    startRecording();
    return undefined;
  }, [phase, startRecording]);

  useEffect(() => {
    if (phase !== 'running') return undefined;
    heartbeatIntervalRef.current = setInterval(async () => {
      try {
        const hb = await otcHeartbeat(otc);
        if (hb?.expires_at) {
          applyServerTimer({ expires_at: hb.expires_at, server_now: hb.server_now });
        }
      } catch (e) {
        const err = e?.response?.data?.detail?.error;
        if (err === 'seat_mismatch' || err === 'seat_lease_held') {
          stopTracksOnly();
          setSeatBlocked(e?.response?.data?.detail?.message || 'Session locked');
        } else if (e?.response?.status === 410) {
          finalizeAssessmentRef.current?.(true);
        }
      }
    }, 30_000);
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [phase, otc, applyServerTimer, stopTracksOnly]);

  const begin = async () => {
    try {
      const started = await otcStart(otc);
      applyServerTimer({
        assessment: { expires_at: started.expires_at },
        server_now: started.server_now,
      });
      await refetch();
      setPhase('running');
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      if (err === 'seat_mismatch') {
        setSeatBlocked(e?.response?.data?.detail?.message);
        return;
      }
      toast.error(detailMessage(e, 'Could not start the session'));
    }
  };

  const finalizeAssessment = useCallback(async (auto = false) => {
    if (terminalInFlightRef.current || submitting || submittedRef.current) return;
    terminalInFlightRef.current = true;
    submittedRef.current = true;
    stopBackgroundSync();
    setSubmitting(true);
    try {
      await prepareRecordingBlob();
      const expectRecording = auto
        && Boolean(runningStartedAtRef.current)
        && (Date.now() - runningStartedAtRef.current) > 5000;
      const uploadOutcome = await ensureUploadBeforeTerminal({ auto, expectRecording });
      if (uploadOutcome?.cancelled) {
        submittedRef.current = false;
        return;
      }
      await flushAnswers();
      const payload = {
        auto_submitted: auto,
        answers: Object.entries(answersRef.current).map(([qid, idx]) => ({
          question_id: qid, selected_index: idx,
        })),
      };
      await otcTechSubmit(otc, payload);
      completeTerminalSuccess();
    } catch (e) {
      if (e?.response?.status === 410) {
        completeTerminalSuccess();
        return;
      }
      toast.error(detailMessage(e, 'Submit failed'));
      submittedRef.current = false;
    } finally {
      terminalInFlightRef.current = false;
      setSubmitting(false);
    }
  }, [
    submitting,
    stopBackgroundSync,
    prepareRecordingBlob,
    ensureUploadBeforeTerminal,
    flushAnswers,
    otc,
    completeTerminalSuccess,
  ]);

  useEffect(() => {
    finalizeAssessmentRef.current = finalizeAssessment;
  }, [finalizeAssessment]);

  const confirmQuit = async () => {
    if (terminalInFlightRef.current || submitting) return;
    terminalInFlightRef.current = true;
    stopBackgroundSync();
    setSubmitting(true);
    try {
      await prepareRecordingBlob();
      const uploadOutcome = await ensureUploadBeforeTerminal();
      if (uploadOutcome?.cancelled) return;
      const out = await otcAbandon(otc);
      setQuitOpen(false);
      setPhase('abandoned');
      navigateAfterExit(out.quit_url || quitUrl, 'abandoned');
    } catch (e) {
      toast.error(detailMessage(e, 'Could not quit assessment'));
    } finally {
      terminalInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const submitButtonLabel = () => {
    if (!submitting) return 'Submit assessment';
    if (uploading) {
      return uploadProgress != null
        ? `Uploading recording (${uploadProgress}%)…`
        : 'Uploading recording…';
    }
    return 'Submitting…';
  };

  if (seatBlocked) {
    return (
      <Center
        icon={AlertTriangle}
        title="Session locked"
        desc={seatBlocked || 'This assessment is open elsewhere. Ask HR for a reissue to switch devices.'}
      />
    );
  }
  if (state === 'invalid') return <Center icon={AlertTriangle} title="Invalid invitation" desc="The link is not recognised. Please ask the recruiter for a fresh one." />;
  if (state === 'expired') return <Center icon={AlertTriangle} title="Link expired" desc="Please request a fresh link from your recruiter." />;
  if (state === 'revoked') return <Center icon={AlertTriangle} title="Link revoked" desc="This invitation was withdrawn by the recruiter." />;
  if (state === 'consumed' || phase === 'submitted') {
    return <Center icon={CheckCircle2} emerald title="Thank you!" desc="Your responses have been submitted. The HR team will reach out with next steps." />;
  }
  if (phase === 'abandoned') {
    return <Center icon={AlertTriangle} title="Assessment abandoned" desc="You exited the assessment. This attempt is closed. Contact HR if you need a new invitation." />;
  }
  if (stage && stage !== 'TECH_ASSESSMENT') return <Center icon={AlertTriangle} title="Wrong link type" desc="This invitation is for a different assessment stage." />;
  if (qs.length === 0) {
    if (data?.seb_required && !data?.seb_verified) {
      return <Center icon={Loader2} spin title="Loading assessment…" desc="Waiting for Secure Exam Browser verification." />;
    }
    return <Center icon={AlertTriangle} title="No questions configured" desc="Please ask the recruiter to configure questions for your pipeline." />;
  }

  if (phase === 'intro') {
    return (
      <div className="hero-glow min-h-screen"><div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-6">
        <Card className="card-elevated w-full"><CardContent className="p-8 space-y-5" data-testid="tech-assessment-intro">
          <div className="text-center">
            <ClipboardCheck className="mx-auto h-10 w-10 text-primary" />
            <h1 className="mt-2 text-2xl font-semibold">Tech Assessment</h1>
            <p className="mt-1 text-sm text-muted-foreground">{data.candidate?.full_name}</p>
          </div>
          <div className="rounded-xl border bg-secondary/40 p-4 text-sm">
            <ul className="space-y-1.5">
              <li className="flex items-center gap-2"><span className="font-medium">Questions:</span> {qs.length}</li>
              <li className="flex items-center gap-2"><span className="font-medium">Time limit:</span> {fmtMMSS(totalSeconds)} (one timer for the full test)</li>
              <li className="text-xs text-muted-foreground">Your camera and microphone will record this session for HR review. Allow access when prompted — the assessment still continues if recording is unavailable.</li>
              <li className="text-xs text-muted-foreground">When the timer expires, any unanswered questions will be auto-submitted. Progress is saved automatically.</li>
            </ul>
          </div>
          <div className="flex items-center justify-end">
            <Button onClick={begin} data-testid="tech-assessment-begin-button">Begin assessment</Button>
          </div>
        </CardContent></Card>
      </div></div>
    );
  }

  const answeredCount = Object.keys(answers).length;
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <header className="sticky top-0 z-10 border-b bg-card">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-6 py-3">
          <div className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-foreground font-bold text-xs">H</div>
          <p className="text-xs font-semibold">Kadel Labs · Tech Assessment</p>
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border bg-secondary px-3 py-1 text-xs font-semibold tabular-nums" data-testid="tech-assessment-timer">
            <Clock className="h-3.5 w-3.5" /> {secondsLeft !== null ? fmtMMSS(secondsLeft) : '--:--'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-rose-700 hover:bg-rose-50"
            onClick={() => setQuitOpen(true)}
            data-testid="tech-assessment-quit-button"
          >
            <LogOut className="h-3.5 w-3.5" /> Quit
          </Button>
        </div>
      </header>
      <main className="mx-auto grid max-w-4xl gap-6 px-6 py-8">
        <Card className="card-elevated"><CardContent className="p-6">
          <p className="text-xs text-muted-foreground">Answered {answeredCount} of {qs.length}</p>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full bg-primary transition-all" style={{ width: `${(answeredCount / qs.length) * 100}%` }} />
          </div>
        </CardContent></Card>

        {qs.map((q, i) => {
          const sel = answers[q.id];
          return (
            <Card key={q.id} className="card-elevated" data-testid={`tech-assessment-question-${i}`}><CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Question {i + 1}</p>
              <p className="mt-1 text-base font-medium leading-snug">{q.text}</p>
              <div className="mt-4 space-y-2">
                {q.options.map((opt, idx) => {
                  const active = sel === idx;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => selectAnswer(q.id, idx)}
                      className={classNames(
                        'flex w-full items-center gap-3 rounded-lg border p-3 text-left text-sm transition-colors',
                        active ? 'border-primary bg-primary/10' : 'bg-card hover:bg-accent'
                      )}
                      data-testid={`tech-assessment-q${i}-opt${idx}`}
                    >
                      <span className={classNames('grid h-6 w-6 shrink-0 place-items-center rounded-full border text-[11px] font-semibold', active ? 'border-primary bg-primary text-primary-foreground' : '')}>{String.fromCharCode(65 + idx)}</span>
                      <span className="flex-1">{opt}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent></Card>
          );
        })}

        <div className="flex items-center justify-end gap-3">
          <div className="text-right">
            <RecordingUploadProgress progress={uploadProgress} uploading={uploading} />
            <p className="text-xs text-muted-foreground">Answers save automatically. You can change them before the timer expires.</p>
          </div>
          <Button onClick={() => finalizeAssessment(false)} disabled={submitting || uploading} data-testid="tech-assessment-submit-button">
            {submitButtonLabel()}
          </Button>
        </div>
      </main>

      <RecordingUploadFailedModal
        open={uploadFailedOpen}
        errorMessage={lastUploadError}
        onRetry={retryUpload}
        onContinue={continueWithoutRecording}
        onCancel={() => {
          cancelUploadPrompt();
          setSubmitting(false);
          submittedRef.current = false;
        }}
      />

      {quitOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="tech-assessment-quit-modal">
          <Card className="w-full max-w-md card-elevated">
            <CardContent className="space-y-4 p-6">
              <h2 className="text-lg font-semibold">Quit assessment?</h2>
              <p className="text-sm text-muted-foreground">
                If you quit now, this attempt will be marked Abandoned. You will not receive a score,
                and you cannot resume this invitation. Ask HR for a reissue if you need another attempt.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setQuitOpen(false)} data-testid="tech-assessment-quit-cancel">
                  Continue assessment
                </Button>
                <Button variant="destructive" onClick={confirmQuit} data-testid="tech-assessment-quit-confirm">
                  Quit and abandon
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function Center({ icon: Icon, title, desc, spin = false, emerald = false }) {
  return (
    <div className="hero-glow min-h-screen"><div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-6">
      <Card className="card-elevated"><CardContent className="p-8 text-center" data-testid="tech-assessment-status-card">
        <Icon className={classNames('mx-auto h-10 w-10', spin && 'animate-spin', emerald ? 'text-emerald-500' : 'text-muted-foreground')} />
        <h1 className="mt-3 text-2xl font-semibold">{title}</h1>
        {desc && <p className="mt-1 text-sm text-muted-foreground">{desc}</p>}
      </CardContent></Card>
    </div></div>
  );
}
